import type { ImgHTMLAttributes } from 'react'
import { assetUrl, assetAvifUrl, assetWebpUrl } from '@/lib/assetUrl'

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> & {
  /** Manifest key / public/ path, e.g. 'photos/brand-art/glow-dark.jpg' */
  src: string
}

/**
 * <img> wrapped in <picture> offering the AVIF/WebP variants that
 * `assets:sync` pre-generates (docs/object-storage.md) — the browser picks
 * the smallest format it supports. Falls back to a bare <img> when the
 * manifest has no variants for the key (dev with CDN unset, SVGs, files not
 * yet synced).
 *
 * The <picture> wrapper gets `display: contents` so layout classes on the
 * <img> (absolute positioning, w-full/h-full) behave exactly as if the
 * wrapper didn't exist.
 */
export default function AssetPicture({ src, alt = '', ...imgProps }: Props) {
  const avif = assetAvifUrl(src)
  const webp = assetWebpUrl(src)
  // eslint-disable-next-line @next/next/no-img-element -- variants are pre-generated at sync time; see imageLoader.ts for why Next's optimizer is bypassed.
  const img = <img src={assetUrl(src)} alt={alt} {...imgProps} />
  if (!avif && !webp) return img
  return (
    <picture className="contents">
      {avif && <source type="image/avif" srcSet={avif} />}
      {webp && <source type="image/webp" srcSet={webp} />}
      {img}
    </picture>
  )
}
